import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Search, FileText, Sparkles, ArrowLeft } from "lucide-react";
import { listTemplates, assignTemplateToUser } from "@/integrations/backend/workout-templates";
import { createAssignedWorkout } from "@/integrations/backend/workout-assigned";
import { format } from "date-fns";
import { toast } from "sonner";

interface AssignWorkoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  assignmentDate: Date | null;
  onSelectTemplate: (assignedWorkoutId: string) => void;
  onCreateFromScratch: () => void;
}

export default function AssignWorkoutDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  assignmentDate,
  onSelectTemplate,
  onCreateFromScratch,
}: AssignWorkoutDialogProps) {
  const [step, setStep] = useState<"choice" | "template">("choice");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDifficultyLevel, setSelectedDifficultyLevel] = useState<string>("All");
  const [assigning, setAssigning] = useState(false);

  const templatesQueryKey = queryKeys.workoutTemplates.list(selectedDifficultyLevel);
  const templatesQuery = useQuery({
    queryKey: templatesQueryKey,
    queryFn: () => listTemplates(selectedDifficultyLevel),
    enabled: open && step === "template",
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...templatesQueryKey]),
  });
  const templates = templatesQuery.data ?? [];
  const loading = templatesQuery.isPending;

  useEffect(() => {
    if (templatesQuery.isError && templatesQuery.error) {
      toast.error("Could not load templates", { description: (templatesQuery.error as Error)?.message ?? String(templatesQuery.error) });
    }
  }, [templatesQuery.isError, templatesQuery.error]);

  // Reset step when dialog closes
  useEffect(() => {
    if (!open) {
      setStep("choice");
      setSearchTerm("");
      setSelectedDifficultyLevel("All");
    }
  }, [open]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) =>
      (t.name || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [templates, searchTerm]);

  const handleSelectTemplate = () => {
    setStep("template");
  };

  const handleUseTemplate = async (templateId: string) => {
    // Default to today's date if no date is provided
    const dateToUse = assignmentDate || new Date();
    
    setAssigning(true);
    try {
      // Pass single difficulty level or undefined for "All"
      const difficultyLevels = selectedDifficultyLevel === "All" 
        ? undefined 
        : [selectedDifficultyLevel];
      
      const assignedWorkoutId = await assignTemplateToUser(
        templateId,
        clientId,
        dateToUse,
        difficultyLevels
      );
      onSelectTemplate(assignedWorkoutId);
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Could not assign workout", { description: error?.message ?? String(error) });
    } finally {
      setAssigning(false);
    }
  };

  const handleCreateFromScratch = async () => {
    // Default to today's date if no date is provided
    const dateToUse = assignmentDate || new Date();
    
    setAssigning(true);
    try {
      const assignedWorkoutId = await createAssignedWorkout(
        clientId,
        dateToUse,
        "Untitled Workout",
        30
      );
      onSelectTemplate(assignedWorkoutId);
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Could not create workout", { description: error?.message ?? String(error) });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogScrollContent className="bg-white text-black sm:max-w-[700px]">
        {step === "choice" ? (
          <>
            <DialogHeader className="shrink-0">
              <DialogTitle>Assign Workout</DialogTitle>
              <DialogDescription>
                Assign to {clientName}
                {assignmentDate && ` on ${format(assignmentDate, "MMM d, yyyy")}`}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Card
                className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
                onClick={handleSelectTemplate}
              >
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Create from Template
                  </h3>
                  <p className="text-sm text-gray-600">
                    Start with a pre-built workout template and customize as needed
                  </p>
                </CardContent>
              </Card>
              <Card
                className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-primary"
                onClick={handleCreateFromScratch}
              >
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Create from Scratch
                  </h3>
                  <p className="text-sm text-gray-600">
                    Build a custom workout from the ground up
                  </p>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>Select Template</DialogTitle>
                  <DialogDescription>
                    Choose a template to use as a starting point
                  </DialogDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep("choice")}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>
            </DialogHeader>
            <DialogBody className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Difficulty Level</Label>
                <RadioGroup
                  value={selectedDifficultyLevel}
                  onValueChange={setSelectedDifficultyLevel}
                  className="flex flex-wrap gap-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="All" id="difficulty-all" />
                    <Label
                      htmlFor="difficulty-all"
                      className="text-sm font-normal cursor-pointer"
                    >
                      All
                    </Label>
                  </div>
                  {(["Beginner", "Intermediate", "Expert", "Pro"] as const).map((level) => (
                    <div key={level} className="flex items-center space-x-2">
                      <RadioGroupItem value={level} id={`difficulty-${level}`} />
                      <Label
                        htmlFor={`difficulty-${level}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {level}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading templates...</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-2">
                    {searchTerm ? "No templates found" : "No templates available"}
                  </p>
                  {!searchTerm && (
                    <p className="text-sm text-gray-400">
                      You can still create a workout from scratch
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto">
                  {filteredTemplates.map((template) => (
                    <Card key={template.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col gap-3">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">
                              {template.name}
                            </h4>
                            <p className="text-sm text-gray-600">
                              Duration: {template.duration_minutes ?? 0} min
                            </p>
                          </div>
                          <Button
                            className="w-full bg-gradient-to-r from-primary to-lime-hover"
                            onClick={() => handleUseTemplate(template.id)}
                            disabled={assigning}
                          >
                            {assigning ? "Assigning..." : "Use Template"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </DialogBody>
          </>
        )}
      </DialogScrollContent>
    </Dialog>
  );
}


